import React, { useEffect, useMemo, useState } from "react";
import styles from "./asset/PracticeModuleTool.module.scss";
import { useSelector } from "react-redux";
import { questionData$ } from "../../../../reduxStore/selectors/questionSelectors";

const isObjectId = (value) => /^[0-9a-fA-F]{24}$/.test(value || "");
const PRACTICE_QUEUE_STORAGE_KEY = "practice_module_question_queue";
const PRACTICE_QUEUE_UPDATED_EVENT = "practice-module-queue-updated";

const loadQueueFromStorage = () => {
    try {
        const rawQueue = localStorage.getItem(PRACTICE_QUEUE_STORAGE_KEY);
        const parsedQueue = rawQueue ? JSON.parse(rawQueue) : [];
        return Array.isArray(parsedQueue) ? parsedQueue : [];
    } catch (error) {
        console.error("Failed to parse practice queue from storage:", error);
        return [];
    }
};

const persistQueueToStorage = (queue) => {
    try {
        localStorage.setItem(PRACTICE_QUEUE_STORAGE_KEY, JSON.stringify(queue));
        if (typeof window !== "undefined") {
            window.dispatchEvent(
                new CustomEvent(PRACTICE_QUEUE_UPDATED_EVENT, {
                    detail: { queueCount: queue.length },
                }),
            );
        }
    } catch (error) {
        console.error("Failed to persist practice queue:", error);
    }
};

const QuestionIdNavigatorTab = ({
    currentQuestionId = "",
    is25QuestionMode = false,
    answeredCount = 0,
    questionLimit = 25,
}) => {
    const [questionId, setQuestionId] = useState(currentQuestionId || "");
    const [touched, setTouched] = useState(false);
    const [message, setMessage] = useState("");
    const [questionQueue, setQuestionQueue] = useState(loadQueueFromStorage);
    const questionResponseData = useSelector(questionData$);

    const normalizedQuestionId = useMemo(() => questionId.trim(), [questionId]);
    const canPrepareRequest = normalizedQuestionId.length > 0;
    const hasValidObjectIdFormat = isObjectId(normalizedQuestionId);
    const remainingQueueCapacity = is25QuestionMode
        ? Math.max(questionLimit - answeredCount - questionQueue.length, 0)
        : Infinity;
    const isQueueAtCapacity = is25QuestionMode && remainingQueueCapacity <= 0;
    const notFoundQueuedQuestionId = questionResponseData?.notFoundQueuedQuestionId || null;

    useEffect(() => {
        setQuestionQueue(loadQueueFromStorage());
    }, [questionResponseData?.question?._id, notFoundQueuedQuestionId]);

    const handleAddToQueue = () => {
        setTouched(true);

        if (!canPrepareRequest) {
            setMessage("Please enter a question ID before adding to queue.");
            return;
        }

        if (isQueueAtCapacity) {
            setMessage(
                `Queue limit reached for ${questionLimit}-question mode. Answered (${answeredCount}) + queued (${questionQueue.length}) cannot exceed ${questionLimit}.`,
            );
            return;
        }

        const alreadyQueued = questionQueue.some((item) => item.questionId === normalizedQuestionId);
        if (alreadyQueued) {
            setMessage("This question is already in the queue.");
            return;
        }

        const nextQueueItem = {
            questionId: normalizedQuestionId,
            objectIdFormat: hasValidObjectIdFormat,
            queuedAt: new Date().toLocaleTimeString(),
        };

        setQuestionQueue((prev) => {
            const updatedQueue = [...prev, nextQueueItem];
            persistQueueToStorage(updatedQueue);
            return updatedQueue;
        });
        setMessage("Question added to queue.");
        setQuestionId("");
    };

    const handleRemoveFromQueue = (questionIdToRemove) => {
        setQuestionQueue((prev) => {
            const updatedQueue = prev.filter((item) => item.questionId !== questionIdToRemove);
            persistQueueToStorage(updatedQueue);
            return updatedQueue;
        });
        setMessage("Question removed from queue.");
    };

    const handleClearQueue = () => {
        setQuestionQueue([]);
        persistQueueToStorage([]);
        setMessage("Queue cleared.");
    };

    return (
        <div className={styles.navigatorTab}>
            <div className={styles.navigatorLayout}>
                <div className={styles.navigatorControls}>
                    <div className={styles.fieldBlock}>
                        <label htmlFor="practice-module-question-id" className={styles.inputLabel}>
                            Question ID
                        </label>
                        <input
                            id="practice-module-question-id"
                            type="text"
                            className={styles.textInput}
                            placeholder="e.g. 67f0a7b2e4d95f1c2a38d1ab"
                            value={questionId}
                            onChange={(event) => setQuestionId(event.target.value)}
                            onBlur={() => setTouched(true)}
                        />
                        {touched && canPrepareRequest && !hasValidObjectIdFormat && (
                            <p className={styles.validationText}>
                                This does not look like a standard MongoDB ObjectId. You can still queue it for now.
                            </p>
                        )}
                        {currentQuestionId && (
                            <p className={styles.captionText}>Current question context: {currentQuestionId}</p>
                        )}
                    </div>

                    <div className={styles.actionButtons}>
                        <button className={styles.btnPrimary} onClick={handleAddToQueue} type="button">
                            Add To Queue
                        </button>
                        <button
                            className={styles.btnSecondary}
                            onClick={handleClearQueue}
                            type="button"
                            disabled={questionQueue.length === 0}
                        >
                            Clear Queue
                        </button>
                    </div>

                    {message && <div className={`${styles.alert} ${styles.alertInfo}`}>{message}</div>}

                    {notFoundQueuedQuestionId && (
                        <div className={`${styles.alert} ${styles.alertError}`}>
                            Queued question not found and skipped: {notFoundQueuedQuestionId}
                        </div>
                    )}

                    {is25QuestionMode && (
                        <div className={styles.queueLimitHint}>
                            25-question mode capacity: Answered {answeredCount} + Queued {questionQueue.length} / {questionLimit}
                            {remainingQueueCapacity !== Infinity && ` (${remainingQueueCapacity} slot(s) left)`}
                        </div>
                    )}
                </div>

                <div className={styles.queuePanel}>
                    <div className={styles.queueHeader}>
                        <h4>Question Queue</h4>
                        <span>{questionQueue.length} item(s)</span>
                    </div>

                    {questionQueue.length === 0 ? (
                        <div className={styles.queueEmpty}>No queued questions yet.</div>
                    ) : (
                        <div className={styles.queueList}>
                            {questionQueue.map((item, index) => (
                                <div className={styles.queueItem} key={item.questionId}>
                                    <div className={styles.queueOrder}>#{index + 1}</div>
                                    <div className={styles.queueMeta}>
                                        <div className={styles.queueQuestionId}>{item.questionId}</div>
                                        <div className={styles.queueSubText}>
                                            {item.objectIdFormat ? "Valid ObjectId" : "Non-standard ID"} • Added {item.queuedAt}
                                        </div>
                                    </div>
                                    <button
                                        className={styles.btnWarning}
                                        type="button"
                                        onClick={() => handleRemoveFromQueue(item.questionId)}
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default QuestionIdNavigatorTab;
